import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { FileText, Upload, ClipboardList } from "lucide-react";
import {
  displayPai,
  displayPaiList,
  formatAusili,
  formatInteressi,
  formatPaiScalar,
  isPaiUpload,
  isPaiCartaceo,
  getPaiFileUrl,
} from "../utils/paiDisplay";

function Section({ title, className, children }) {
  return (
    <div className={`border rounded-lg p-4 ${className || ""}`}>
      <h3 className="font-semibold text-slate-700 mb-3">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  const v =
    value === null || value === undefined || value === "" ? "—" : value;
  return (
    <div className="text-sm grid grid-cols-1 md:grid-cols-[minmax(140px,1fr)_2fr] gap-1 md:gap-3">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="font-medium text-slate-800 break-words">{v}</span>
    </div>
  );
}

/**
 * Lettura completa PAI salvato (struttura annidata come da API create_pai).
 * @param {object} pai - il documento PAI
 * @param {string} [fascicoloId] - necessario per mostrare il pulsante "Apri file" sui PAI upload
 * @param {string} [token] - JWT token per autenticare il download
 */
export default function PaiViewBody({ pai, fascicoloId, token }) {
  if (!pai) return null;

  const isUpload = isPaiUpload(pai);
  const isCartaceo = isPaiCartaceo(pai);

  const generalInfo = (
    <div className="bg-purple-50 p-4 rounded-lg">
      <h3 className="font-semibold text-purple-700 mb-3">Informazioni generali</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div>
          <span className="text-slate-500">Stato:</span>{" "}
          <Badge className="bg-emerald-500">{pai.stato || "—"}</Badge>
        </div>
        <div>
          <span className="text-slate-500">Redatto da:</span>{" "}
          {pai.redatto_da || "—"}
        </div>
        <div>
          <span className="text-slate-500">Data redazione:</span>{" "}
          {pai.data_redazione
            ? new Date(pai.data_redazione).toLocaleDateString("it-IT")
            : "—"}
        </div>
        <div>
          <span className="text-slate-500">Scadenza:</span>{" "}
          {pai.data_scadenza
            ? new Date(pai.data_scadenza).toLocaleDateString("it-IT")
            : "—"}
        </div>
      </div>
    </div>
  );

  if (isUpload) {
    return (
      <div className="space-y-6">
        {generalInfo}

        <div className="border rounded-lg p-6 bg-blue-50 border-blue-200 text-center space-y-3">
          <div className="flex items-center justify-center gap-2 text-blue-700 font-semibold">
            <Upload className="w-5 h-5" />
            PAI inserito tramite file caricato
          </div>
          <p className="text-sm text-slate-600">
            {pai.file_name && <span>File: <strong>{pai.file_name}</strong></span>}
          </p>
          {fascicoloId && token && (
            <Button
              variant="outline"
              className="border-blue-500 text-blue-600 hover:bg-blue-100"
              onClick={() => window.open(getPaiFileUrl(fascicoloId, token), '_blank', 'noopener,noreferrer')}
            >
              <FileText className="w-4 h-4 mr-2" />
              Apri file PAI
            </Button>
          )}
          {pai.note_generali && (
            <div className="mt-2 text-left border-t border-blue-200 pt-3">
              <p className="text-xs text-slate-500 mb-1">Note</p>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{pai.note_generali}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isCartaceo) {
    return (
      <div className="space-y-6">
        {generalInfo}

        <div className="border rounded-lg p-6 bg-purple-50 border-purple-200 text-center space-y-3">
          <div className="flex items-center justify-center gap-2 text-purple-700 font-semibold">
            <ClipboardList className="w-5 h-5" />
            PAI cartaceo attestato in archivio fisico
          </div>
          <p className="text-sm text-slate-600">
            Nessun file digitale caricato. L&apos;attestazione indica che il PAI redatto in cartaceo
            è custodito in archivio della struttura.
          </p>
          {pai.attestato_by_name && (
            <p className="text-xs text-slate-500">
              Attestato da {pai.attestato_by_name}
              {pai.attestato_at && (
                <> il {new Date(pai.attestato_at).toLocaleDateString("it-IT")}</>
              )}
            </p>
          )}
        </div>
      </div>
    );
  }

  const n = pai.nutrizione || {};
  const r = pai.riposo || {};
  const ig = pai.igiene || {};
  const m = pai.motoria || {};
  const e = pai.educativa || {};
  const o = pai.osservazione || {};
  const og = pai.obiettivi_generali || {};
  const r1 = pai.riferimento1 || {};

  const refLine = [r1.cognome, r1.nome].filter(Boolean).join(" ");
  const refRest = [r1.parentela, r1.telefono].filter(Boolean).join(" · ");

  return (
    <div className="space-y-6">
      <div className="bg-purple-50 p-4 rounded-lg">
        <h3 className="font-semibold text-purple-700 mb-3">
          Informazioni generali
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <span className="text-slate-500">Stato:</span>{" "}
            <Badge className="bg-emerald-500">{pai.stato || "—"}</Badge>
          </div>
          <div>
            <span className="text-slate-500">Redatto da:</span>{" "}
            {pai.redatto_da || "—"}
          </div>
          <div>
            <span className="text-slate-500">Data redazione:</span>{" "}
            {pai.data_redazione
              ? new Date(pai.data_redazione).toLocaleDateString("it-IT")
              : "—"}
          </div>
          <div>
            <span className="text-slate-500">Scadenza:</span>{" "}
            {pai.data_scadenza
              ? new Date(pai.data_scadenza).toLocaleDateString("it-IT")
              : "—"}
          </div>
        </div>
      </div>

      <Section title="Stato civile, invalidità e medico">
        <Row label="Stato civile" value={displayPai(pai.stato_civile, "stato_civile")} />
        <Row label="Invalidità civile" value={displayPai(pai.invalidita_civile, "invalidita_civile")} />
        <Row
          label="Indennità accompagnamento"
          value={displayPai(pai.indennita_accompagnamento, "indennita_accompagnamento")}
        />
        <Row label="Cert. L.104/92" value={displayPai(pai.certificazione_104, "certificazione_104")} />
        <Row label="Medico curante" value={pai.medico_curante || "—"} />
        <Row label="Contatti medico" value={pai.medico_contatti || "—"} />
        <Row
          label="Situazione personale e familiare"
          value={pai.situazione_personale || "—"}
        />
        {(refLine || refRest) && (
          <Row
            label="Persona di riferimento 1"
            value={[refLine, refRest].filter(Boolean).join(" — ") || "—"}
          />
        )}
      </Section>

      <Section title="Area nutrizione" className="border-amber-200/80">
        <Row label="Appetito" value={displayPai(n.appetito, "appetito")} />
        <Row label="Percezione sete" value={displayPai(n.percezione_sete, "percezione_sete")} />
        <Row label="Masticazione" value={displayPai(n.masticazione, "masticazione")} />
        <Row label="Deglutizione" value={displayPai(n.deglutizione, "deglutizione")} />
        <Row label="Preparazione cibi" value={displayPai(n.preparazione_cibi, "preparazione_cibi")} />
        <Row label="Tipo di alimentazione" value={displayPaiList(n.alimentazione)} />
        <Row label="Colazione — autonomia" value={displayPai(n.colazione_autonomia, "colazione_autonomia")} />
        <Row
          label="Colazione — somm. terapia"
          value={formatPaiScalar(n.colazione_terapia, "colazione_terapia") ?? "—"}
        />
        <Row label="Pranzo — autonomia" value={displayPai(n.pranzo_autonomia, "pranzo_autonomia")} />
        <Row
          label="Pranzo — somm. terapia"
          value={formatPaiScalar(n.pranzo_terapia, "pranzo_terapia") ?? "—"}
        />
        <Row label="Cena — autonomia" value={displayPai(n.cena_autonomia, "cena_autonomia")} />
        <Row
          label="Cena — somm. terapia"
          value={formatPaiScalar(n.cena_terapia, "cena_terapia") ?? "—"}
        />
        <Row label="Note nutrizione" value={n.note || "—"} />
        <Row label="Obiettivi area nutrizione" value={n.obiettivi || "—"} />
      </Section>

      <Section title="Area riposo" className="border-blue-200/80">
        <Row label="Sonno" value={displayPai(r.sonno, "sonno")} />
        <Row label="Cambio postura" value={displayPai(r.cambio_postura, "cambio_postura")} />
        <Row label="N. cuscini" value={displayPai(r.cuscini, "cuscini")} />
        <Row label="Sponde letto" value={displayPai(r.sponde_letto, "sponde_letto")} />
        <Row label="Controllo notturno" value={displayPai(r.controllo_notturno, "controllo_notturno")} />
        <Row label="Posizione seduta" value={displayPai(r.posizione_seduta, "posizione_seduta")} />
        <Row label="Messa a letto" value={displayPai(r.messa_a_letto, "messa_a_letto")} />
        <Row label="Difficoltà respiratorie" value={displayPaiList(r.difficolta_respiratoria)} />
        <Row label="Obiettivi area riposo" value={r.obiettivi || "—"} />
      </Section>

      <Section title="Area igiene" className="border-cyan-200/80">
        <Row label="Alzata mattino" value={displayPai(ig.alzata_mattino, "alzata_mattino")} />
        <Row label="Vestizione" value={displayPai(ig.vestizione, "vestizione")} />
        <Row label="Rimessa a letto" value={displayPai(ig.rimessa_a_letto, "rimessa_a_letto")} />
        <Row label="Igiene superiore" value={displayPai(ig.igiene_superiore, "igiene_superiore")} />
        <Row label="Igiene intima" value={displayPai(ig.igiene_intima, "igiene_intima")} />
        <Row label="Bagno settimanale" value={displayPai(ig.bagno_settimanale, "bagno_settimanale")} />
        <Row label="Approccio al bagno" value={displayPai(ig.approccio_bagno, "approccio_bagno")} />
        <Row label="Incontinenza urinaria" value={displayPai(ig.incontinenza_urinaria, "incontinenza_urinaria")} />
        <Row label="Incontinenza rettale" value={displayPai(ig.incontinenza_rettale, "incontinenza_rettale")} />
        <Row label="Usa il bagno" value={displayPai(ig.usa_bagno, "usa_bagno")} />
        <Row label="Usa catetere" value={displayPai(ig.usa_catetere, "usa_catetere")} />
        <Row label="Usa pannolone" value={displayPai(ig.usa_pannolone, "usa_pannolone")} />
        <Row label="Allergie pannolone" value={displayPai(ig.allergie_pannolone, "allergie_pannolone")} />
        <Row label="Note igiene" value={ig.note || "—"} />
        <Row label="Obiettivi area igiene" value={ig.obiettivi || "—"} />
      </Section>

      <Section title="Area motoria" className="border-green-200/80">
        <Row label="Spostamenti / deambulazione" value={displayPai(m.spostamenti, "spostamenti")} />
        <Row label="Ausili" value={formatAusili(m.ausili) || "—"} />
        <Row label="Presidi personalizzati" value={displayPaiList(m.presidi)} />
        <Row label="Programma mattina" value={m.programma_mattina || "—"} />
        <Row label="Programma pomeriggio" value={m.programma_pomeriggio || "—"} />
        <Row label="Obiettivi area motoria" value={m.obiettivi || "—"} />
      </Section>

      <Section title="Area educativa" className="border-indigo-200/80">
        <Row label="Comprensione" value={displayPai(e.comprensione, "comprensione")} />
        <Row label="Comunicazione" value={displayPai(e.comunicazione, "comunicazione")} />
        <Row label="Vita di relazione" value={displayPai(e.vita_relazione, "vita_relazione")} />
        <Row label="Interessi e attitudini" value={formatInteressi(e.interessi) || "—"} />
        <Row label="Esce dalla struttura" value={displayPai(e.esce_struttura, "esce_struttura")} />
        <Row label="Luoghi frequentati" value={displayPaiList(e.luoghi_frequentati)} />
      </Section>

      <Section title="Osservazione" className="border-rose-200/80">
        <Row label="Tono dell'umore" value={displayPai(o.tono_umore, "tono_umore")} />
        <Row label="Agitazione psico-motoria" value={displayPai(o.agitazione, "agitazione")} />
        <Row label="Collaborazione" value={displayPai(o.collaborazione, "collaborazione")} />
        <Row label="Orientamento" value={displayPai(o.orientamento, "orientamento")} />
        <Row label="Memoria" value={displayPai(o.memoria, "memoria")} />
        <Row label="Ansia" value={displayPaiList(o.ansia)} />
        {o.descrizione ? <Row label="Osservazioni (testo libero)" value={o.descrizione} /> : null}
      </Section>

      <Section title="Obiettivi generali" className="bg-purple-50 border-purple-200">
        <div className="flex flex-wrap gap-2">
          {og.socializzazione && (
            <Badge className="bg-purple-600">Incremento socializzazione</Badge>
          )}
          {og.mantenimento && (
            <Badge className="bg-purple-600">Mantenimento capacità residue</Badge>
          )}
          {og.autonomie && (
            <Badge className="bg-purple-600">Rinforzo autonomie personali</Badge>
          )}
          {!og.socializzazione && !og.mantenimento && !og.autonomie && (
            <span className="text-sm text-slate-500">Nessun obiettivo generale selezionato</span>
          )}
        </div>
        <Row label="Obiettivi specifici" value={og.specifico || "—"} />
        {og.descrizione ? <Row label="Descrizione (testo libero)" value={og.descrizione} /> : null}
      </Section>

      <div className="border rounded-lg p-4 bg-slate-50">
        <h3 className="font-semibold text-slate-700 mb-2">Note generali</h3>
        <p className="text-sm text-slate-600 whitespace-pre-wrap">
          {pai.note_generali || "—"}
        </p>
      </div>
    </div>
  );
}
